
import { timeTextLabel } from '../../utils/shiftTime';
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { api, ShiftType, Store, Staff } from '../../api';
import Modal from '../ui/Modal';
import Dialog from '../ui/Dialog';
import Input from '../ui/Input';
import { useToast } from '../../contexts/ToastContext';

interface ShiftPatternSettingsTabProps {
  stores: Store[];
  myProfile: Staff | null;
  isSystemAdmin: boolean;
}

const ShiftPatternSettingsTab: React.FC<ShiftPatternSettingsTabProps> = ({ stores: allStores, myProfile, isSystemAdmin }) => {
  const { showToast } = useToast();
  const [shiftTypes, setShiftTypes] = useState<ShiftType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterStoreId, setFilterStoreId] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<Partial<ShiftType>>({});
  const [timeStart, setTimeStart] = useState('09:00');
  const [timeEnd, setTimeEnd] = useState('18:00');
  const [dialog, setDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  const labelClass = "text-xs font-bold text-slate-500 mb-1.5 block uppercase tracking-wider";
  const inputClass = "w-full bg-white border border-slate-300 rounded-[4px] px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all shadow-sm font-bold";

  const availableStores = allStores.filter(s => isSystemAdmin || myProfile?.stores?.includes(s.id));

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // 修正されたAPIを呼び出し。新旧データ構造をマージして取得。
      const types = await api.getShiftTypes();
      setShiftTypes(types);
    } catch (e) {
      showToast('データの取得に失敗しました。', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    if (!editingType.name || !editingType.storeIds || editingType.storeIds.length === 0) {
        showToast('名称と対象店舗を選択してください。', 'error');
        return;
    }
    setIsLoading(true);
    try {
      const type = {
        ...editingType,
        id: editingType.id || Date.now(),
        time: `${timeStart}-${timeEnd}`,
        abbr: editingType.abbr || editingType.name?.slice(0, 1),
        storeId: editingType.storeIds[0], // 後方互換性維持
        storeIds: editingType.storeIds
      } as ShiftType;
      await api.saveShiftTypes([type]);
      fetchData();
      setIsModalOpen(false);
      showToast('保存しました。', 'success');
    } catch (e) {
      showToast('保存に失敗しました。', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (id: number) => {
    setDialog({
      isOpen: true,
      title: '削除確認',
      message: 'このシフト設定を削除しますか？',
      onConfirm: async () => {
        try {
          await api.deleteShiftType(id);
          setShiftTypes(prev => prev.filter(t => t.id !== id));
          showToast('削除しました。', 'success');
        } catch (e) {
          showToast('削除に失敗しました。', 'error');
        }
        setDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const filteredTypes = shiftTypes.filter(t => {
      // 新旧データ形式（storeIds配列 または storeId文字列）の両方で所属判定を行う
      const currentTypeStoreIds = t.storeIds || (t.storeId ? [t.storeId] : []);
      const isMemberOfStore = currentTypeStoreIds.some(sid => availableStores.some(as => as.id === sid));
      const matchesFilter = filterStoreId === 'all' || currentTypeStoreIds.includes(filterStoreId);
      return isMemberOfStore && matchesFilter;
  });

  return (
    <div className="space-y-6 animate-fade-in-up">
      <Dialog {...dialog} type="confirm" isDestructive onCancel={() => setDialog(prev => ({ ...prev, isOpen: false }))} />
      
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-bold text-slate-800">シフト一覧</h2>
          <select value={filterStoreId} onChange={(e) => setFilterStoreId(e.target.value)} className="bg-white border border-gray-300 rounded-[4px] px-3 py-2 text-sm font-bold text-slate-600">
            <option value="all">全店舗を表示</option>
            {availableStores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <button onClick={() => { setEditingType({ storeIds: [availableStores[0]?.id] }); setTimeStart('09:00'); setTimeEnd('18:00'); setIsModalOpen(true); }} className="bg-blue-600 text-white font-bold px-4 py-2 rounded-[4px] text-sm flex items-center shadow-md active:scale-95"><Plus size={16} className="mr-1"/>新規作成</button>
      </div>

      <div className="bg-white shadow-sm border border-gray-200 overflow-hidden rounded-[4px]">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-slate-500 font-bold border-b border-gray-200 text-xs uppercase tracking-wider">
            <tr><th className="p-4">シフト名</th><th className="p-4">時間</th><th className="p-4">適用店舗</th><th className="p-4 text-center">操作</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr><td colSpan={4} className="p-8 text-center text-slate-400"><Loader2 className="animate-spin inline mr-2" size={20}/>読み込み中...</td></tr>
            ) : filteredTypes.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-slate-400 font-bold text-xs uppercase tracking-widest">シフト設定がありません</td></tr>
            ) : filteredTypes.map(type => (
              <tr key={type.id} className="hover:bg-gray-50 transition-colors">
                <td className="p-4 font-bold text-slate-800">{type.name}（{type.abbr}）{type.code && <span className="ml-2 text-[11px] font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 whitespace-nowrap">コード {type.code}</span>}</td>
                <td className="p-4 font-mono">{timeTextLabel(type.time)}</td>
                <td className="p-4 text-slate-500 text-xs">{(type.storeIds || [type.storeId]).map(sid => availableStores.find(s => s.id === sid)?.name).filter(Boolean).join(', ')}</td>
                <td className="p-4 text-center">
                  <div className="flex justify-center gap-2">
                    <button onClick={() => { setEditingType(type); setTimeStart(type.time.split('-')[0]); setTimeEnd(type.time.split('-')[1]); setIsModalOpen(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-[4px]"><Edit size={18} /></button>
                    <button onClick={() => handleDelete(type.id)} className="p-2 text-gray-400 hover:text-red-600 rounded-[4px]"><Trash2 size={18} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="シフトの登録・編集" maxWidth="max-w-md">
        <div className="space-y-6 pt-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Input label="シフト名" value={editingType.name || ''} placeholder="例：早番、ランチ" onChange={(e) => setEditingType({...editingType, name: e.target.value})} /></div>
            <div><Input label="略称" value={editingType.abbr || ''} placeholder="早" maxLength={2} onChange={(e) => setEditingType({...editingType, abbr: e.target.value})} /></div>
          </div>
          <div>
            <Input label="シフトコード（勤怠・人事システム連携用）" value={editingType.code || ''} placeholder="例：A1" onChange={(e) => setEditingType({...editingType, code: e.target.value.trim()})} />
            <p className="text-xs text-slate-500 -mt-4">シフト表のCSV出力で、この時間のシフトにこのコードを付けます。</p>
          </div>

          <div>
            <label className={labelClass}>対象店舗</label>
            <div className="flex flex-wrap gap-2 p-3 bg-slate-50 border border-slate-200 rounded-[4px]">
                {availableStores.map(store => {
                    const isSelected = (editingType.storeIds || (editingType.storeId ? [editingType.storeId] : [])).includes(store.id);
                    return (
                        <button
                            key={store.id}
                            onClick={() => {
                                const current = editingType.storeIds || (editingType.storeId ? [editingType.storeId] : []);
                                const next = isSelected ? current.filter(id => id !== store.id) : [...current, store.id];
                                setEditingType({ ...editingType, storeIds: next });
                            }}
                            className={`px-3 py-1.5 rounded-[4px] text-[10px] font-bold border transition-all ${isSelected ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
                        >
                            {store.name}
                        </button>
                    );
                })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelClass}>開始時間</label><input type="time" value={timeStart} onChange={(e) => setTimeStart(e.target.value)} className={inputClass} /></div>
            <div><label className={labelClass}>終了時間</label><input type="time" value={timeEnd} onChange={(e) => setTimeEnd(e.target.value)} className={inputClass} /></div>
          </div>
          <div className="pt-6 flex justify-end gap-3 border-t">
            <button onClick={() => setIsModalOpen(false)} className="px-6 py-2.5 text-slate-500 font-bold">キャンセル</button>
            <button onClick={handleSave} disabled={isLoading} className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded-[4px] shadow-md flex items-center gap-2">
              {isLoading && <Loader2 size={16} className="animate-spin" />}保存
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ShiftPatternSettingsTab;
